import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyValue } from '@mews/enums';
import { useGainsightService } from '@mews/framework';
import {
    CalculateIndexAction,
    handleAssistantSearchListArrowKey,
    handleAssistantSearchListTabKey,
} from '../helpers/assistantKeyboardNavigationHelper';

export interface AssistantListItemSelectionStateProps {
    selectedIndex: number;
    selectedTabIndex: number;
    isTabNavigation: boolean;
    openSummary: boolean;
}

export const assistantListItemSelectionInitialState: AssistantListItemSelectionStateProps = {
    selectedIndex: 0, // Index of the selected item using Arrow up/down Keys (Search result list in the drawer)
    selectedTabIndex: 0, // Index of selected item using Tab key (Focusable elements in the drawer - Search result list + focusable elements like buttons, anything with Tabindex 0)
    isTabNavigation: false, // Flag to check if the navigation is done using Tab key instead of Arrow keys
    openSummary: false, // Open summary panel using Arrow left key
};

export const useAssistantListItemSelectionHandler = (
    focused?: boolean,
    focusSearchInput?: () => void,
    showRecent?: boolean
) => {
    const gainSightService = useGainsightService();
    const [focusableElements, setFocusableElements] = useState<HTMLElement[]>([]);
    const [listItemSelectionState, setListItemSelectionState] = useState<AssistantListItemSelectionStateProps>(
        assistantListItemSelectionInitialState
    );
    const { isTabNavigation, selectedTabIndex, selectedIndex } = listItemSelectionState;

    const handleListItemsUnFocused = useCallback(() => {
        setListItemSelectionState((prev) => ({
            ...prev,
            ...assistantListItemSelectionInitialState,
        }));
    }, []);

    const resetTabIndex = useCallback(() => {
        if (focusableElements.length > 0) {
            focusableElements.forEach((el, index) => {
                if (index > 0) {
                    el.tabIndex = -1;
                    el.blur();
                }
            });
            if (showRecent) {
                const listItemElementRemoveButtons = focusableElements.filter((listItem) =>
                    listItem.className.includes('remove-search-prompt-button')
                );
                listItemElementRemoveButtons?.forEach((listItemElementRemoveButton) => {
                    listItemElementRemoveButton.style.visibility = 'hidden';
                });
            }
        }
    }, [focusableElements, showRecent]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const element = e.target as HTMLElement;
            switch (e.key) {
                case KeyValue.Tab: {
                    handleAssistantSearchListTabKey({
                        focusableElements,
                        setListItemSelectionState,
                        action: e.shiftKey ? CalculateIndexAction.Decrement : CalculateIndexAction.Increment,
                        element,
                        event: e,
                    });
                    break;
                }
                case KeyValue.ArrowUp: {
                    handleAssistantSearchListArrowKey({
                        event: e,
                        element,
                        setListItemSelectionState,
                        action: CalculateIndexAction.Decrement,
                        focusableElements,
                        resetTabIndex,
                        focusSearchInput,
                    });
                    break;
                }
                case KeyValue.ArrowDown: {
                    handleAssistantSearchListArrowKey({
                        event: e,
                        element,
                        setListItemSelectionState,
                        action: CalculateIndexAction.Increment,
                        focusableElements,
                        resetTabIndex,
                        focusSearchInput,
                    });
                    break;
                }
                case KeyValue.ArrowRight: {
                    if (!showRecent) {
                        setListItemSelectionState((prev) => ({
                            ...prev,
                            openSummary: false,
                        }));
                    }
                    break;
                }
                case KeyValue.ArrowLeft: {
                    if (!showRecent) {
                        const focusableItemIndex = focusableElements.findIndex((el) => el === element);
                        if (focusableItemIndex > 0) {
                            const listItemElements = focusableElements.filter((el) =>
                                el.className.includes('drawer-list-item')
                            );
                            const drawerListItemIndex = listItemElements.findIndex((el) => el === element);
                            setListItemSelectionState((prev) => ({
                                ...prev,
                                selectedIndex: drawerListItemIndex + 1,
                                openSummary: true,
                            }));
                        } else {
                            setListItemSelectionState((prev) => ({
                                ...prev,
                                openSummary: true,
                            }));
                        }
                    }
                    break;
                }
                case KeyValue.Enter: {
                    const focusableItemIndex = focusableElements.findIndex((el) => el === element);
                    if (focusableItemIndex > 0) {
                        e.stopPropagation();
                        e.preventDefault();
                        element.click();
                    } else if (selectedIndex > 0) {
                        const listItemElements = focusableElements.filter((el) =>
                            el.className.includes('drawer-list-item')
                        );
                        const listItem = listItemElements[selectedIndex - 1];
                        if (listItem) {
                            e.stopPropagation();
                            e.preventDefault();
                            listItem.click();
                            gainSightService.trackEvent('AssistantShortcutEnterSelectCard');
                        }
                    }
                    break;
                }
                default:
                    break;
            }
        };

        if (focusableElements.length > 0) {
            focusableElements.forEach((el) => {
                el.addEventListener('keydown', handleKeyDown);
            });
        }

        return () => {
            focusableElements.forEach((el) => {
                el.removeEventListener('keydown', handleKeyDown);
            });
        };
    }, [focusSearchInput, focusableElements, resetTabIndex, selectedIndex, showRecent, gainSightService]);

    /** selectedListItemElement change  */
    useEffect(() => {
        if (!focused) {
            return;
        }

        if (focusableElements.length > 0 && selectedTabIndex > 0) {
            if (selectedTabIndex < focusableElements.length) {
                resetTabIndex();
            }

            const item = focusableElements[selectedTabIndex];
            if (item) {
                item.tabIndex = 0;
                if (isTabNavigation) {
                    const removeSearchPromptButton = item.className.includes('remove-search-prompt-button');
                    if (showRecent && removeSearchPromptButton) {
                        item.style.visibility = 'visible';
                    }
                    item.focus();
                }
            }
        } else if (focusableElements.length > 0 && selectedTabIndex < 1 && selectedIndex < 1) {
            resetTabIndex();
            if (focusSearchInput) {
                focusSearchInput();
            }
        }
    }, [
        selectedTabIndex,
        isTabNavigation,
        resetTabIndex,
        focusableElements,
        focusSearchInput,
        selectedIndex,
        showRecent,
        focused,
    ]);

    return useMemo(
        () => ({
            focusableElements,
            listItemSelectionState,
            setListItemSelectionState,
            handleListItemsUnFocused,
            setFocusableElements,
        }),
        [focusableElements, handleListItemsUnFocused, listItemSelectionState, setFocusableElements]
    );
};
